import React, {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {ObjUtil} from 'yougile-util'
import {
  DataObjectId,
  getRangeValue,
  ReportStickerFilter,
  ReportStickerRangePrefix,
  ReportStickerRangePrefixType,
  StickerData
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Input} from '@components/common/base/input'
import {InputApi} from '@components/common/base/input/types'
import {LinkButtonSmall} from '@components/common/button/button'
import {
  DropdownList,
  DropdownMultiSelectList,
  getMultiSelectText
} from '@components/common/dropdown-list/dropdown-list'
import {Option} from '@components/common/select/select'
import {Remove, Separator} from '@components/statistics/stats-reports/common'
import {cn} from '@containers/html-utils'
import {getAllStickers} from '@extensions/sticker/sticker-data-extractor'

interface OuterProps {
  filter: ReportStickerFilter
}

interface Props extends OuterProps {
  update: (filters: ReportStickerFilter) => void
}

const getStates = (sticker: StickerData): Option[] =>
  ObjUtil.list(sticker?.states?.index, (stateId: string, state) => ({
    value: stateId,
    label: state.name
  }))

const getStickerValueOptions = (sticker?: StickerData): Option[] => {
  if (!sticker) {
    return []
  }

  if (['text', 'numeric'].includes(sticker.type)) {
    const options = [
      {
        label: Lang.msg.component.stickerTextFilter.allFilled,
        value: 'all-filled'
      },
      {
        label: Lang.msg.component.stickerTextFilter.allEmpty,
        value: 'not-set'
      }
    ]

    if (sticker.type === 'text') {
      return options
    }

    return [
      ...options,
      {
        label: Lang.msg.component.stickerNumericFilter.setRange,
        value: 'set-range'
      }
    ]
  }

  const stickerOptions: Option[] = getStates(sticker)
  stickerOptions.splice(
    0,
    0,
    {
      label: Lang.msg.component.stickerFilter.notPinned,
      value: 'not-pinned'
    },
    {
      label: Lang.msg.component.stickerFilter.notSet,
      value: 'not-set'
    }
  )
  return stickerOptions
}

const StickerSelector = (props: {
  allStickers: StickerData[]
  stickerId: DataObjectId
  values: (string | 'not-set')[]
  initialOpen: boolean
  updateSelector: (
    stickerId: DataObjectId,
    values?: (string | 'not-set')[]
  ) => void
}) => {
  const options: Option<DataObjectId>[] = props.allStickers.map(x => ({
    label: x.title,
    value: x.id
  }))
  const sticker = props.allStickers.find(x => x.id === props.stickerId)
  const showRange = props.values.includes('set-range')

  const handleRangeChange = (
    api: InputApi,
    prefix: ReportStickerRangePrefixType
  ) => {
    let found = false

    const result = props.values.map(value => {
      if (value.startsWith(prefix)) {
        found = true
        return `${prefix}${api.text}`
      }

      return value
    })

    if (!found) {
      result.push(`${prefix}${api.text}`)
    }

    props.updateSelector(props.stickerId, result)
  }

  const stickerValueOptions = getStickerValueOptions(sticker)

  return (
    <div className="flex w-full">
      <DropdownList
        className="w-176"
        initialOpen={props.initialOpen}
        value={props.stickerId}
        options={options}
        onChange={id => props.updateSelector(id)}
      />
      <Separator />
      <div className="w-full max-w-272">
        {sticker?.type === 'numeric' ? (
          <DropdownList<string>
            className="w-full"
            onChange={value => props.updateSelector(props.stickerId, [value])}
            value={props.values[0]}
            options={stickerValueOptions}
          />
        ) : (
          <DropdownMultiSelectList
            className="w-full"
            onChange={values => props.updateSelector(props.stickerId, values)}
            value={props.values}
            options={stickerValueOptions}
          />
        )}
        {showRange && (
          <div className="flex max-w-272 mt-8">
            <Input
              inputClassName="w-1/2 h-24"
              height="24"
              type="number"
              defaultValue={getRangeValue(
                props.values,
                ReportStickerRangePrefix.from
              )}
              onChange={api =>
                handleRangeChange(api, ReportStickerRangePrefix.from)
              }
              placeholder={Lang.msg.from}
              autoFocus
            />
            <div className="w-16 text-center">-</div>
            <Input
              inputClassName="w-1/2 h-24"
              height="24"
              type="number"
              defaultValue={getRangeValue(
                props.values,
                ReportStickerRangePrefix.to
              )}
              onChange={api =>
                handleRangeChange(api, ReportStickerRangePrefix.to)
              }
              placeholder={Lang.msg.to}
              autoFocus
            />
          </div>
        )}
      </div>
    </div>
  )
}

// eslint-disable-next-line max-lines-per-function
export const StickerFilter = (props: Props) => {
  const filter = props.filter ?? {}
  const box = Sync.getCurrentSandbox()
  const allStickers = getAllStickers(box, true).filter(x => !x.isSystem)
  const availableStickers = allStickers.filter(x => !filter[x.id])
  const [initialOpen, setInitialOpen] = useState(false)
  const stickers = Object.entries(filter)
  const addNewSticker = () => {
    setInitialOpen(true)
    if (!availableStickers.length) {
      props.update({...filter})
    } else {
      props.update({
        ...filter,
        [availableStickers[0].id]: {
          values: getStickerValueOptions(availableStickers[0]).map(
            ({value: id}) => ({id})
          )
        }
      })
    }
  }
  useEffect(() => {
    if (!props.filter) {
      addNewSticker()
    }
  }, [])

  return (
    <div>
      {stickers.length > 0 && (
        <div className="space-y-8">
          {stickers.map(([stickerId, values], i) => {
            const selectorStickers = [
              ...allStickers.filter(x => x.id === stickerId),
              ...availableStickers
            ]
            return (
              <div key={i} className={cn('flex justify-between')}>
                <StickerSelector
                  initialOpen={initialOpen && i === stickers.length - 1}
                  allStickers={selectorStickers}
                  stickerId={stickerId as DataObjectId}
                  values={values.values.map(x => x.id)}
                  updateSelector={(newStickerId, values) => {
                    const newFilter = {...filter}
                    delete newFilter[stickerId]
                    const sticker = allStickers.find(x => x.id === newStickerId)
                    const stickerOptions = getStickerValueOptions(sticker).map(
                      ({value: id}) => ({id})
                    )
                    const valuesBasedOnOptions =
                      sticker?.type === 'numeric'
                        ? [stickerOptions[0]]
                        : stickerOptions

                    newFilter[newStickerId] = {
                      values:
                        stickerId !== newStickerId || !values
                          ? valuesBasedOnOptions
                          : values.map(id => ({id}))
                    }
                    props.update(newFilter)
                  }}
                />

                <Remove
                  onClick={() => {
                    const newFilter = {...filter}
                    delete newFilter[stickerId]
                    props.update(
                      Object.keys(newFilter).length ? newFilter : undefined
                    )
                  }}
                />
              </div>
            )
          })}
        </div>
      )}
      {availableStickers.length > 0 && (
        <LinkButtonSmall className="mt-4 ml-4" onClick={addNewSticker}>
          {Lang.msg.statAddSticker}
        </LinkButtonSmall>
      )}
    </div>
  )
}

export const StickerFilterValue = (props: OuterProps) => {
  const filter = props.filter ?? {}
  const box = Sync.getCurrentSandbox()
  const allStickers = getAllStickers(box, true).filter(x => !x.isSystem)
  const availableStickers = allStickers.filter(x => !filter[x.id])
  const stickers = Object.entries(filter)

  const elements = stickers.map(([stickerId, values]) => {
    const selectorStickers = [
      ...allStickers.filter(x => x.id === stickerId),
      ...availableStickers
    ]

    const sticker = selectorStickers.find(x => x.id === stickerId)

    const stickerValueOptions = getStickerValueOptions(sticker)
    const val = values.values.map(x => x.id)
    return (
      <span className="flex items-center">
        {sticker.title}
        {<Separator />}
        {getMultiSelectText({value: val, options: stickerValueOptions})}
      </span>
    )
  })

  return <>{elements}</>
}
